'use client'

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'

/** @public — resolved theme applied to the DOM */
export type Theme = 'light' | 'dark'
/** @public — user's stored theme preference */
export type ThemePreference = 'light' | 'dark' | 'system'

export type ThemeContextValue = {
  /** Resolved theme currently applied to the DOM. */
  theme: Theme
  /** User's stored preference. Distinct from `theme` only when `'system'`. */
  preference: ThemePreference
  setTheme: (next: ThemePreference) => void
}

export type ThemeProviderProps = {
  children: React.ReactNode
  /** localStorage key for the stored preference. Default: `'theme'`. */
  storageKey?: string
}

const DEFAULT_STORAGE_KEY = 'theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

const ThemeContext: React.Context<ThemeContextValue | null> =
  createContext<ThemeContextValue | null>(null)

// Fallback returned by useTheme() outside a provider so callers (e.g. storybook
// stories or pre-mount renders) don't crash and don't have to null-check.
// `setTheme` is intentionally a no-op when no provider is present.
const FALLBACK_CONTEXT_VALUE: ThemeContextValue = {
  theme: 'light',
  preference: 'system',
  setTheme: () => {
    // no-op outside a provider
  },
}

function readStoredPreference(storageKey: string): ThemePreference {
  if (typeof window === 'undefined') {
    return 'system'
  }
  try {
    const stored = window.localStorage.getItem(storageKey)
    if (stored === 'light' || stored === 'dark') {
      return stored
    }
  } catch {
    // Private-mode Safari / disabled storage / quota errors — treat as unset.
  }
  return 'system'
}

function writeStoredPreference(
  storageKey: string,
  next: ThemePreference,
): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    if (next === 'system') {
      window.localStorage.removeItem(storageKey)
    } else {
      window.localStorage.setItem(storageKey, next)
    }
  } catch {
    // Swallow — see readStoredPreference.
  }
}

function getSystemThemeSnapshot(): Theme {
  if (typeof window === 'undefined') {
    return 'light'
  }
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function getServerSystemTheme(): Theme {
  return 'light'
}

function subscribeToSystemTheme(callback: () => void): () => void {
  const mediaQuery = window.matchMedia(DARK_QUERY)
  mediaQuery.addEventListener('change', callback)
  return () => mediaQuery.removeEventListener('change', callback)
}

function resolveTheme(preference: ThemePreference, systemTheme: Theme): Theme {
  return preference === 'system' ? systemTheme : preference
}

export function ThemeProvider({
  children,
  storageKey = DEFAULT_STORAGE_KEY,
}: ThemeProviderProps): React.JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readStoredPreference(storageKey),
  )
  const systemTheme = useSyncExternalStore(
    subscribeToSystemTheme,
    getSystemThemeSnapshot,
    getServerSystemTheme,
  )
  const theme = resolveTheme(preference, systemTheme)

  useLayoutEffect(() => {
    const root = document.documentElement
    root.classList.add('theme-transitioning')
    root.classList.toggle('dark', theme === 'dark')
    // Force a layout flush so the no-transition state is committed before
    // scheduling removal — otherwise the next-tick removal could race the
    // paint and per-component transitions would fire on the dark-class flip.
    root.getBoundingClientRect()
    setTimeout(() => root.classList.remove('theme-transitioning'), 0)
  }, [theme])

  const setTheme = useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next)
      writeStoredPreference(storageKey, next)
    },
    [storageKey],
  )

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, preference, setTheme }),
    [theme, preference, setTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? FALLBACK_CONTEXT_VALUE
}
